import { useEffect, useState, useCallback } from "react";
import { TrendingUp, IndianRupee, ShoppingBag, Receipt } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { api, inr } from "@/lib/api";

const COLORS = ["#0B8E36", "#FF6B00", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F59E0B", "#EF4444"];

const KPI = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid={`an-kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span><div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon size={16} /></div></div>
    <p className="text-2xl font-extrabold text-slate-900 tracking-tight mt-2">{value}</p>
  </div>
);

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const load = useCallback(() => api.get(`/admin/analytics?days=${days}`).then((r) => setD(r.data)), [days]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="text-slate-400">Loading analytics…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2"><TrendingUp className="text-emerald-600" /> Sales Analytics</h1>
        <select data-testid="an-days" value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-semibold">
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPI icon={IndianRupee} label="Total Sales" value={inr(d.total_sales)} color="bg-emerald-100 text-emerald-700" />
        <KPI icon={ShoppingBag} label="Orders" value={d.order_count} color="bg-orange-100 text-orange-700" />
        <KPI icon={Receipt} label="Avg Order Value" value={inr(d.avg_order_value)} color="bg-sky-100 text-sky-700" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h2 className="font-bold mb-4">Sales Trend — Online vs POS</h2>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={d.trend}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" /><XAxis dataKey="date" fontSize={11} interval="preserveStartEnd" /><YAxis fontSize={11} /><Tooltip formatter={(v) => inr(v)} /><Legend />
            <Line type="monotone" dataKey="online" stroke="#0B8E36" strokeWidth={2.5} dot={false} name="Online" />
            <Line type="monotone" dataKey="pos" stroke="#FF6B00" strokeWidth={2.5} dot={false} name="POS" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-bold mb-4">Sales by Branch</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.by_store}><XAxis dataKey="name" fontSize={10} /><YAxis fontSize={10} /><Tooltip formatter={(v) => inr(v)} /><Bar dataKey="value" radius={[6, 6, 0, 0]}>{d.by_store.map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}</Bar></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-bold mb-4">Sales by Category</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={d.by_category} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={false}>{d.by_category.map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => inr(v)} /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-bold mb-4">Channel Split</h2>
          <div className="space-y-3 mt-6">
            {d.by_channel.map((c, i) => (
              <div key={c.name} data-testid={`an-channel-${c.name}`}>
                <div className="flex justify-between text-sm font-semibold mb-1"><span className="capitalize">{c.name}</span><span>{inr(c.value)}</span></div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${d.total_sales ? (c.value / d.total_sales) * 100 : 0}%`, background: COLORS[i % COLORS.length] }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
